import classNames from "classnames";
import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import { fetchWithAuth } from "../../../../../api-client/APIClient";
import { CommonModelSyncStatus, LinkedAccount } from "../../../../../models/Entities";
import { displayNameForAPICategoryIntegration } from "../../../../../models/Helpers";
import AccountTypeBadge from "../../../../shared/AccountTypeBadge";
import SyncHistoryTable from "../../../linked-accounts/detail-page/overview/SyncHistoryTable";
import useInterval from "../../../../shared/hooks/useInterval";
import useTimeout from "../../../../shared/hooks/useTimeout";
import { showErrorToast, showSuccessToast } from "../../../../shared/Toasts";
import SampleSyncHistoryTable from "./SampleSyncHistoryTable";
import { Text, Button, ButtonVariant, Typeahead } from "@merge-api/merge-javascript-shared";
import LinkedAccountTypeaheadRenderOption from "../../../../LinkedAccountTypeaheadRenderOption";

export interface Props {
  linkedAccount: LinkedAccount | null;
  linkedAccounts: LinkedAccount[] | null;
  setLinkedAccount: Dispatch<SetStateAction<LinkedAccount | null>>;
}

/**
 * Shows a header + resync button for sync history. Resync just shortcircuits the
 * interval to fetch data now.
 */
const SyncHistoryView = ({ linkedAccount, linkedAccounts, setLinkedAccount }: Props) => {
  const [syncStatuses, setSyncStatuses] = useState<CommonModelSyncStatus[] | null>(null);
  const [isResyncing, setIsResyncing] = useState(false);
  const [isResyncingVisuallyOnly, setIsResyncingVisuallyOnly] = useState(false);

  /**
   * For the account id, fetch sync status with support for cancellation.
   */

  useEffect(() => {
    if (linkedAccount) {
      fetchSyncStatusForSelectedLinkedAccount();
    }
  }, [linkedAccount]);

  const fetchSyncStatusForSelectedLinkedAccount = () => {
    let isCancelled = false;
    fetchWithAuth({
      path: `/integrations/linked-accounts/${linkedAccount?.id}/sync-status`,
      method: "GET",
      onResponse: (data: CommonModelSyncStatus[]) => {
        if (isCancelled) {
          return;
        }
        setSyncStatuses(data);
      },
    });
    return () => {
      isCancelled = true;
    };
  };

  // Forces a resync of the data
  const forceResync = () => {
    if (!linkedAccount || !linkedAccount.completed_at) {
      setIsResyncing(false);
      return;
    }
    let isCancelled = false;
    fetchWithAuth({
      path: `/integrations/linked-accounts/${linkedAccount.id}/force-resync`,
      method: "POST",
      onResponse: (data: CommonModelSyncStatus[]) => {
        if (isCancelled) {
          return;
        }
        setIsResyncing(false);
        setSyncStatuses(data);
        showSuccessToast(
          `Resync queued for ${linkedAccount.end_user.organization_name} integration with ${linkedAccount.integration.name}.`,
        );
      },
      onError: () => {
        if (isCancelled) {
          return;
        }
        setIsResyncing(false);
        showErrorToast("Failed to force resync. Please try again.");
      },
    });
    return () => {
      isCancelled = true;
    };
  };

  // Polls for new sync statuses every 3 seconds if non-demo user. Needs to be inside an anon function
  useInterval(() => {
    if (syncStatuses != null) {
      fetchSyncStatusForSelectedLinkedAccount();
    }
  }, 3000);

  /**
   * This is funny - on a fast network, the resync is so fast there's no visual feedback,
   * so we use this to keep it spinning for just under a second for the user to see
   * one rotation of the animation when they click resync all. There's a visually
   * only state, and an actually syncing state to keep track of both.
   */
  useTimeout(
    () => {
      setIsResyncingVisuallyOnly(false);
    },
    isResyncingVisuallyOnly ? 990 : null,
  );
  // Fetches data on demand - the button spins at least for a second, or until the network call is finished
  const resyncButton = (
    <Button
      size="sm"
      variant={ButtonVariant.TertiaryWhite}
      disabled={!linkedAccount || !linkedAccount.completed_at}
      onClick={() => {
        setIsResyncing(true);
        setIsResyncingVisuallyOnly(true);
        forceResync();
      }}
      leftIcon={
        <RefreshCw
          className={classNames("m-0 p-0", {
            rotating: isResyncingVisuallyOnly || isResyncing,
          })}
          size={12}
        />
      }
    >
      Resync all
    </Button>
  );

  return (
    <div className="flex flex-col">
      <Text variant="h6" className="mb-2">
        Test Linked Account
      </Text>
      <div className="flex flex-row gap-4 flex-wrap lg:flex-nowrap">
        <Typeahead
          className="w-full max-w-md"
          value={linkedAccount}
          options={linkedAccounts || []}
          placeholder="Select Test Linked Account..."
          onChange={(_, linkedAccount) => setLinkedAccount(linkedAccount || null)}
          renderOption={(option: LinkedAccount) => (
            <LinkedAccountTypeaheadRenderOption linkedAccount={option} />
          )}
          getOptionLabel={(option) => option.end_user.organization_name}
          getOptionKey={(option: LinkedAccount) => option.id}
        />

        <div className="flex flex-row justify-between items-center gap-4 w-full">
          {linkedAccount && (
            <Text variant="pre-title" className="text-gray-40">
              {displayNameForAPICategoryIntegration(linkedAccount.category)}
            </Text>
          )}
          {resyncButton}
        </div>
      </div>

      <div className="mt-6">
        {linkedAccount ? (
          <SyncHistoryTable
            linkedAccount={linkedAccount}
            syncStatuses={syncStatuses}
            isOnboardingPage
          />
        ) : (
          <SampleSyncHistoryTable />
        )}
      </div>
    </div>
  );
};

export default SyncHistoryView;
